import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useSnackbar } from 'notistack'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import {
  GuardaPrivacidade,
  mensagemErroPrivacidade,
  rotuloAtor,
  useAcessoPrivacidade,
} from '@/components/privacidade/comum'
import { SolicitacaoTitularDrawer } from '@/components/privacidade/SolicitacaoTitularDrawer'
import { solicitacoesTitularService } from '@/services/solicitacoesTitularService'
import {
  CANAL_SOLICITACAO_LABEL,
  ESTADOS_SOLICITACAO,
  ESTADO_SOLICITACAO_LABEL,
  EstadoSolicitacaoTitular,
  RegistrarSolicitacaoRequest,
  SolicitacaoTitular,
  TIPO_SOLICITACAO_LABEL,
  TipoSolicitacaoTitular,
} from '@/types/privacidade'
import { formatDate, formatDateTime } from '@/utils/formatters'

const TIPOS = Object.keys(TIPO_SOLICITACAO_LABEL) as TipoSolicitacaoTitular[]
const CANAIS_EXTERNOS: RegistrarSolicitacaoRequest['canal'][] = ['EMAIL', 'OUTRO']

const REGISTRO_VAZIO: RegistrarSolicitacaoRequest = {
  tipo: 'ACESSO',
  canal: 'EMAIL',
  titularNome: '',
  titularDocumento: '',
  contatoResposta: '',
  descricao: '',
}

const Conteudo: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const acesso = useAcessoPrivacidade()
  const [filtroEstado, setFiltroEstado] = useState<EstadoSolicitacaoTitular | ''>('')
  const [filtroTipo, setFiltroTipo] = useState<TipoSolicitacaoTitular | ''>('')
  /** Busca por CPF (POST, o CPF não vai em URL): quando ativa, substitui a lista. */
  const [cpfBusca, setCpfBusca] = useState('')
  const [resultadoCpf, setResultadoCpf] = useState<SolicitacaoTitular[] | null>(null)
  const [buscandoCpf, setBuscandoCpf] = useState(false)
  const [lista, setLista] = useState<SolicitacaoTitular[] | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [aberta, setAberta] = useState<string | null>(null)
  const [registro, setRegistro] = useState<RegistrarSolicitacaoRequest | null>(null)
  const [salvando, setSalvando] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setLista(
        await solicitacoesTitularService.listar({
          estado: filtroEstado || undefined,
          tipo: filtroTipo || undefined,
        })
      )
    } catch (e) {
      setErro(mensagemErroPrivacidade(e, 'Falha ao carregar as solicitações'))
      setLista(null)
    } finally {
      setCarregando(false)
    }
  }, [filtroEstado, filtroTipo])

  const buscarPorCpf = async () => {
    const documento = cpfBusca.replace(/\D/g, '')
    if (!documento) return
    setBuscandoCpf(true)
    try {
      setResultadoCpf(await solicitacoesTitularService.buscarPorDocumento(documento))
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, 'Falha na busca por CPF'), { variant: 'error' })
    } finally {
      setBuscandoCpf(false)
    }
  }

  const limparBuscaCpf = () => {
    setCpfBusca('')
    setResultadoCpf(null)
  }

  useEffect(() => {
    carregar()
  }, [carregar])

  const registrar = async () => {
    if (!registro) return
    setSalvando(true)
    try {
      const doc = registro.titularDocumento?.replace(/\D/g, '')
      const criada = await solicitacoesTitularService.registrar({
        ...registro,
        titularNome: registro.titularNome.trim(),
        titularDocumento: doc || undefined,
        contatoResposta: registro.contatoResposta.trim(),
        descricao: registro.descricao?.trim() || undefined,
      })
      enqueueSnackbar(`Solicitação ${criada.protocolo} registrada`, { variant: 'success' })
      setRegistro(null)
      await carregar()
      setAberta(criada.protocolo)
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, 'Falha ao registrar a solicitação'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  // Prazo só aparece como coluna se alguma solicitação tiver (vazio = não configurado).
  const exibida = resultadoCpf ?? lista
  const temPrazo = (exibida ?? []).some((s) => !!s.prazoRespostaEm)

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Solicitações do titular
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pedidos de titulares (Art. 18 da LGPD) vindos do portal ou registrados pelo DPO. Estados só andam para
            frente; a linha do tempo é imutável.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setRegistro({ ...REGISTRO_VAZIO })}
          sx={{ flexShrink: 0 }}
        >
          Registrar solicitação recebida por e-mail
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Estado"
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value as EstadoSolicitacaoTitular | '')}
          sx={{ minWidth: 200 }}
          disabled={!!resultadoCpf}
        >
          <MenuItem value="">Todos</MenuItem>
          {ESTADOS_SOLICITACAO.map((e) => (
            <MenuItem key={e} value={e}>
              {ESTADO_SOLICITACAO_LABEL[e]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Tipo"
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value as TipoSolicitacaoTitular | '')}
          sx={{ minWidth: 200 }}
          disabled={!!resultadoCpf}
        >
          <MenuItem value="">Todos</MenuItem>
          {TIPOS.map((t) => (
            <MenuItem key={t} value={t}>
              {TIPO_SOLICITACAO_LABEL[t]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Buscar por CPF do titular"
          value={cpfBusca}
          onChange={(e) => setCpfBusca(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') buscarPorCpf()
          }}
          inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
        />
        <Button onClick={buscarPorCpf} disabled={buscandoCpf || !cpfBusca.replace(/\D/g, '')}>
          Buscar
        </Button>
        {resultadoCpf && <Button onClick={limparBuscaCpf}>Limpar busca</Button>}
      </Stack>

      {resultadoCpf ? (
        resultadoCpf.length === 0 ? (
          <EstadoDeLista estado="vazio" mensagem="Nenhuma solicitação para este CPF" />
        ) : null
      ) : carregando && !lista ? (
        <EstadoDeLista estado="carregando" />
      ) : erro ? (
        <EstadoDeLista estado="erro" detalhe={erro} acao={{ label: 'Tentar de novo', onClick: carregar }} />
      ) : !resultadoCpf && lista && lista.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma solicitação"
          detalhe={filtroEstado ? 'Nada neste estado.' : 'Nenhuma solicitação de titular registrada.'}
        />
      ) : null}
      {exibida && exibida.length > 0 && !(erro && !resultadoCpf) && (
        <TabelaAcao<SolicitacaoTitular>
          aria-label="Solicitações do titular"
          colunas={[
            { titulo: 'Protocolo', render: (s) => <strong>{s.protocolo}</strong> },
            { titulo: 'Tipo', render: (s) => TIPO_SOLICITACAO_LABEL[s.tipo] ?? s.tipo },
            {
              titulo: 'Estado',
              render: (s) => <Chip size="small" variant="outlined" label={ESTADO_SOLICITACAO_LABEL[s.estado]} />,
            },
            { titulo: 'Canal', render: (s) => CANAL_SOLICITACAO_LABEL[s.canal] ?? s.canal },
            {
              titulo: 'Responsável',
              render: (s) => (s.responsavelId ? rotuloAtor(s.responsavelId, acesso.usuarioId) : '—'),
            },
            { titulo: 'Recebida em', render: (s) => formatDateTime(s.recebidaEm) },
            ...(temPrazo
              ? [{ titulo: 'Prazo informativo', render: (s: SolicitacaoTitular) => (s.prazoRespostaEm ? formatDate(s.prazoRespostaEm) : '—') }]
              : []),
          ]}
          linhas={exibida}
          chaveDaLinha={(s) => s.protocolo}
          onAbrir={(s) => setAberta(s.protocolo)}
          rotuloAcessivelAcao={(s) => `Abrir ${s.protocolo}`}
        />
      )}

      <SolicitacaoTitularDrawer protocolo={aberta} onFechar={() => setAberta(null)} onAlterado={carregar} />

      <Dialog open={!!registro} onClose={() => (salvando ? undefined : setRegistro(null))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Registrar solicitação recebida fora do portal</DialogTitle>
        {registro && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  select
                  label="Tipo"
                  value={registro.tipo}
                  onChange={(e) => setRegistro({ ...registro, tipo: e.target.value as TipoSolicitacaoTitular })}
                  sx={{ flex: 1 }}
                >
                  {TIPOS.map((t) => (
                    <MenuItem key={t} value={t}>
                      {TIPO_SOLICITACAO_LABEL[t]}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Canal"
                  value={registro.canal}
                  onChange={(e) =>
                    setRegistro({ ...registro, canal: e.target.value as RegistrarSolicitacaoRequest['canal'] })
                  }
                  sx={{ minWidth: 140 }}
                >
                  {CANAIS_EXTERNOS.map((c) => (
                    <MenuItem key={c} value={c}>
                      {CANAL_SOLICITACAO_LABEL[c]}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                label="Nome do titular"
                required
                value={registro.titularNome}
                onChange={(e) => setRegistro({ ...registro, titularNome: e.target.value })}
              />
              <TextField
                label="CPF do titular (opcional)"
                value={registro.titularDocumento ?? ''}
                onChange={(e) => setRegistro({ ...registro, titularDocumento: e.target.value })}
                helperText="Não é gravado em claro: o servidor guarda só um pseudônimo e os 2 últimos dígitos."
                inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
              />
              <TextField
                label="Contato para resposta"
                required
                value={registro.contatoResposta}
                onChange={(e) => setRegistro({ ...registro, contatoResposta: e.target.value })}
              />
              <TextField
                label="Data de recebimento"
                type="date"
                value={registro.recebidaEm ?? ''}
                onChange={(e) => setRegistro({ ...registro, recebidaEm: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
                helperText="Vazio = hoje. Data futura é recusada."
              />
              <TextField
                label="Descrição do pedido"
                multiline
                rows={3}
                value={registro.descricao ?? ''}
                onChange={(e) => setRegistro({ ...registro, descricao: e.target.value })}
              />
              <Alert severity="info">O protocolo é gerado pelo servidor ao registrar.</Alert>
            </Stack>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRegistro(null)} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={registrar}
            disabled={
              salvando ||
              !registro?.titularNome.trim() ||
              !registro?.contatoResposta.trim()
            }
          >
            {salvando ? 'Registrando…' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

/** Solicitações do titular (AN-12 §4, §12): ADMIN e DPO; ações de estado só do DPO. */
export const SolicitacoesTitularPage: FC = () => (
  <GuardaPrivacidade>
    <Conteudo />
  </GuardaPrivacidade>
)

export default SolicitacoesTitularPage
